import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, Plus, Trash2, Pencil, Download, Upload, FileText, Check, ListChecks, ToggleLeft, Sparkles, Settings, Save,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TIPOS = {
  multiple_choice: { label: 'Selección múltiple', color: '#0F766E', icon: ListChecks },
  true_false:      { label: 'Verdadero / Falso',  color: '#D97706', icon: ToggleLeft },
  concept_match:   { label: 'Seleccione el concepto', color: '#7C3AED', icon: Sparkles },
};

export default function ConfigurarPruebaModal({ curso, token, onClose }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [data, setData] = useState({ preguntas: [], curso: null, total: 0 });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [cfgEdit, setCfgEdit] = useState({ intentos_maximos: 3, porcentaje_aprobacion: 85, preguntas_por_prueba: '' });
  const [uploading, setUploading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/capacitador/cursos/${curso.id}/prueba`, { headers });
      setData(r.data);
      setCfgEdit({
        intentos_maximos: r.data.curso.intentos_maximos,
        porcentaje_aprobacion: r.data.curso.porcentaje_aprobacion,
        preguntas_por_prueba: r.data.curso.preguntas_por_prueba ?? '',
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cargar prueba');
    }
    setLoading(false);
  }, [curso.id, token]); // eslint-disable-line

  useEffect(() => { reload(); }, [reload]);

  const saveConfig = async () => {
    try {
      const payload = {
        intentos_maximos: cfgEdit.intentos_maximos,
        porcentaje_aprobacion: cfgEdit.porcentaje_aprobacion,
        preguntas_por_prueba: cfgEdit.preguntas_por_prueba === '' || cfgEdit.preguntas_por_prueba === null
          ? null
          : parseInt(cfgEdit.preguntas_por_prueba, 10),
      };
      await axios.put(`${API}/capacitador/cursos/${curso.id}/prueba/config`, payload, { headers });
      toast.success('Configuración guardada');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (preg) => {
    if (!window.confirm(`¿Eliminar pregunta "${preg.pregunta.substring(0, 50)}..."?`)) return;
    try {
      await axios.delete(`${API}/capacitador/preguntas/${preg.id}`, { headers });
      toast.success('Pregunta eliminada');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    }
  };

  const descargarPlantilla = async () => {
    try {
      const res = await axios.get(`${API}/capacitador/cursos/${curso.id}/preguntas/template`,
        { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `plantilla_preguntas_${curso.nombre.substring(0, 20)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Plantilla descargada');
    } catch {
      toast.error('Error al descargar plantilla');
    }
  };

  const importarExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      const r = await axios.post(`${API}/capacitador/cursos/${curso.id}/preguntas/import`, fd,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      if (r.data.errores?.length) {
        toast.warning(`${r.data.insertadas} insertadas · ${r.data.errores.length} con errores`);
        console.warn('Errores de importación:', r.data.errores);
      } else {
        toast.success(`${r.data.insertadas} preguntas importadas`);
      }
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al importar');
    }
    setUploading(false);
    e.target.value = '';
  };

  return createPortal(
    <div style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
        data-testid="config-prueba-modal">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-teal-600 to-teal-700 text-white flex items-center justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-white/70">Configurar Prueba</p>
            <h2 className="text-lg font-bold">{curso.nombre}</h2>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-lg" data-testid="close-prueba">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Config row */}
        <div className="px-6 py-3 border-b border-slate-100 bg-slate-50 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 text-xs">
            <Settings className="w-4 h-4 text-slate-500" />
            <span className="font-semibold text-slate-600">Configuración:</span>
          </div>
          <label className="text-xs text-slate-700 flex items-center gap-2">
            Intentos máx:
            <input type="number" min="1" max="10" value={cfgEdit.intentos_maximos}
              onChange={e => setCfgEdit({ ...cfgEdit, intentos_maximos: parseInt(e.target.value) || 1 })}
              className="w-16 px-2 py-1 border border-slate-200 rounded text-center text-xs"
              data-testid="cfg-intentos" />
          </label>
          <label className="text-xs text-slate-700 flex items-center gap-2">
            Nota mínima (%):
            <input type="number" min="1" max="100" value={cfgEdit.porcentaje_aprobacion}
              onChange={e => setCfgEdit({ ...cfgEdit, porcentaje_aprobacion: parseInt(e.target.value) || 1 })}
              className="w-16 px-2 py-1 border border-slate-200 rounded text-center text-xs"
              data-testid="cfg-nota-min" />
          </label>
          <label className="text-xs text-slate-700 flex items-center gap-2" title="Preguntas random a presentar al trabajador. Vacío = todas las del banco.">
            Preguntas x prueba:
            <input type="number" min="0" value={cfgEdit.preguntas_por_prueba ?? ''}
              onChange={e => setCfgEdit({ ...cfgEdit, preguntas_por_prueba: e.target.value })}
              placeholder={`${data?.total || 0}`}
              className="w-16 px-2 py-1 border border-violet-200 rounded text-center text-xs bg-violet-50"
              data-testid="cfg-preg-prueba" />
            <span className="text-[10px] text-slate-400">de {data?.total || 0} (random)</span>
          </label>
          <button onClick={saveConfig}
            className="text-xs font-semibold px-3 py-1.5 bg-slate-800 text-white rounded hover:bg-slate-900 inline-flex items-center gap-1"
            data-testid="save-cfg">
            <Save className="w-3 h-3" /> Guardar config
          </button>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={descargarPlantilla}
              className="text-xs font-semibold px-3 py-1.5 border border-slate-300 text-slate-700 rounded hover:bg-slate-100 inline-flex items-center gap-1"
              data-testid="download-template">
              <Download className="w-3 h-3" /> Plantilla Excel
            </button>
            <label className="text-xs font-semibold px-3 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 inline-flex items-center gap-1 cursor-pointer">
              <Upload className="w-3 h-3" /> {uploading ? 'Subiendo…' : 'Importar Excel'}
              <input type="file" accept=".xlsx,.xls" onChange={importarExcel} disabled={uploading}
                className="hidden" data-testid="import-excel" />
            </label>
            <button onClick={() => setEditing({ nuevo: true, tipo: 'multiple_choice', pregunta: '',
              alternativa_a: '', alternativa_b: '', alternativa_c: '', alternativa_d: '', respuesta_correcta: 'A', orden: 0 })}
              className="text-xs font-semibold px-3 py-1.5 bg-teal-600 text-white rounded hover:bg-teal-700 inline-flex items-center gap-1"
              data-testid="new-pregunta">
              <Plus className="w-3 h-3" /> Nueva pregunta
            </button>
          </div>
        </div>

        {/* Lista preguntas */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
          {loading ? (
            <p className="text-center text-sm text-slate-400 py-8">Cargando…</p>
          ) : data.preguntas.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">Aún no hay preguntas para esta prueba.</p>
              <p className="text-xs mt-1">Crea una manualmente o importa un Excel.</p>
            </div>
          ) : (
            data.preguntas.map((p, idx) => {
              const tipoMeta = TIPOS[p.tipo] || TIPOS.multiple_choice;
              const TipoIcon = tipoMeta.icon;
              return (
                <div key={p.id}
                  className="border border-slate-200 rounded-lg p-3 hover:border-slate-300 bg-white"
                  data-testid={`pregunta-row-${p.id}`}>
                  <div className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-600 text-xs font-bold flex items-center justify-center shrink-0">
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                          style={{ backgroundColor: `${tipoMeta.color}15`, color: tipoMeta.color }}>
                          <TipoIcon className="w-3 h-3" /> {tipoMeta.label}
                        </span>
                        <span className="text-[10px] text-slate-400">Orden #{p.orden}</span>
                      </div>
                      <p className="text-sm font-medium text-slate-800 mb-1.5">{p.pregunta}</p>
                      <div className="grid grid-cols-2 gap-1 text-[11px]">
                        {['A', 'B', 'C', 'D'].map(letra => {
                          const val = p[`alternativa_${letra.toLowerCase()}`];
                          if (!val) return null;
                          const esCorrecta = p.respuesta_correcta === letra;
                          return (
                            <div key={letra}
                              className={`px-2 py-1 rounded flex items-start gap-1.5 ${esCorrecta ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'text-slate-600'}`}>
                              <span className="font-bold shrink-0">{letra}.</span>
                              <span className="flex-1">{val}</span>
                              {esCorrecta && <Check className="w-3 h-3 text-emerald-600 shrink-0" />}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => setEditing({ ...p, nuevo: false })}
                        className="p-1.5 hover:bg-slate-100 rounded text-slate-500"
                        data-testid={`edit-pregunta-${p.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleDelete(p)}
                        className="p-1.5 hover:bg-red-50 rounded text-red-500"
                        data-testid={`delete-pregunta-${p.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-between text-xs">
          <span className="text-slate-500">
            <strong className="text-slate-800">{data.total}</strong> pregunta{data.total !== 1 ? 's' : ''} en total
          </span>
          <button onClick={onClose}
            className="text-xs px-4 py-1.5 border border-slate-300 rounded hover:bg-slate-100">
            Cerrar
          </button>
        </div>

        {editing && <PreguntaForm
          curso={curso}
          preg={editing}
          token={token}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />}
      </div>
    </div>,
    document.body
  );
}

function PreguntaForm({ curso, preg, token, onClose, onSaved }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [form, setForm] = useState({
    tipo: preg.tipo || 'multiple_choice',
    pregunta: preg.pregunta || '',
    alternativa_a: preg.alternativa_a || '',
    alternativa_b: preg.alternativa_b || '',
    alternativa_c: preg.alternativa_c || '',
    alternativa_d: preg.alternativa_d || '',
    respuesta_correcta: preg.respuesta_correcta || 'A',
    orden: preg.orden || 0,
  });
  const [saving, setSaving] = useState(false);

  // Auto-llenar alternativas para true_false
  useEffect(() => {
    if (form.tipo === 'true_false') {
      setForm(f => ({
        ...f,
        alternativa_a: 'Verdadero',
        alternativa_b: 'Falso',
        alternativa_c: '',
        alternativa_d: '',
        respuesta_correcta: ['A', 'B'].includes(f.respuesta_correcta) ? f.respuesta_correcta : 'A',
      }));
    }
  }, [form.tipo]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.pregunta.trim()) { toast.error('Ingresa el enunciado'); return; }
    setSaving(true);
    try {
      if (preg.nuevo) {
        await axios.post(`${API}/capacitador/cursos/${curso.id}/preguntas`, form, { headers });
        toast.success('Pregunta creada');
      } else {
        await axios.put(`${API}/capacitador/preguntas/${preg.id}`, form, { headers });
        toast.success('Pregunta actualizada');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
    setSaving(false);
  };

  return createPortal(
    <div style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      onClick={onClose}>
      <form onSubmit={save} onClick={e => e.stopPropagation()}
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        data-testid="pregunta-form">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h3 className="font-bold text-slate-800">{preg.nuevo ? 'Nueva' : 'Editar'} pregunta</h3>
          <button type="button" onClick={onClose} className="text-slate-400"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto">
          <div>
            <label className="text-[11px] font-semibold text-slate-600 mb-1 block">Tipo</label>
            <div className="grid grid-cols-3 gap-1.5">
              {Object.entries(TIPOS).map(([tipoId, meta]) => {
                const Icon = meta.icon;
                const selected = form.tipo === tipoId;
                return (
                  <button key={tipoId} type="button"
                    onClick={() => setForm({ ...form, tipo: tipoId })}
                    data-testid={`tipo-${tipoId}`}
                    className={`text-[11px] font-semibold py-2 px-2 rounded border inline-flex items-center gap-1.5 justify-center ${selected ? 'text-white' : 'bg-white hover:bg-slate-50'}`}
                    style={selected
                      ? { backgroundColor: meta.color, borderColor: meta.color }
                      : { color: meta.color, borderColor: `${meta.color}40` }}>
                    <Icon className="w-3.5 h-3.5" /> {meta.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-600 mb-1 block">
              {form.tipo === 'concept_match' ? 'Descripción del concepto *' : 'Enunciado *'}
            </label>
            <textarea value={form.pregunta}
              onChange={e => setForm({ ...form, pregunta: e.target.value })}
              rows={2}
              placeholder={form.tipo === 'concept_match'
                ? 'Ej: Conjunto de medidas diseñadas para prevenir lesiones al ejecutar una tarea.'
                : 'Ej: ¿Cuál es el EPP obligatorio para trabajos en altura?'}
              className="w-full px-3 py-2 border border-slate-200 rounded text-sm"
              data-testid="input-enunciado" />
          </div>
          <div className="space-y-2">
            <p className="text-[11px] font-semibold text-slate-600">
              {form.tipo === 'true_false' ? 'Respuesta' : form.tipo === 'concept_match' ? 'Conceptos (marca el correcto)' : 'Alternativas (marca la correcta)'}
            </p>
            {['A', 'B', 'C', 'D'].map(letra => {
              const key = `alternativa_${letra.toLowerCase()}`;
              const isDisabled = form.tipo === 'true_false' && ['C', 'D'].includes(letra);
              const isTF = form.tipo === 'true_false';
              const esCorrecta = form.respuesta_correcta === letra;
              if (isDisabled) return null;
              return (
                <div key={letra} className="flex items-center gap-2">
                  <input type="radio" name="correcta" checked={esCorrecta}
                    onChange={() => setForm({ ...form, respuesta_correcta: letra })}
                    data-testid={`correcta-${letra}`}
                    className="w-4 h-4" />
                  <span className={`text-xs font-bold ${esCorrecta ? 'text-emerald-700' : 'text-slate-500'} w-4`}>{letra}.</span>
                  <input type="text" value={form[key]} disabled={isTF}
                    onChange={e => setForm({ ...form, [key]: e.target.value })}
                    placeholder={isTF ? (letra === 'A' ? 'Verdadero' : 'Falso') : `Alternativa ${letra}`}
                    className={`flex-1 px-3 py-1.5 border border-slate-200 rounded text-sm ${isTF ? 'bg-slate-50 text-slate-500' : ''}`}
                    data-testid={`alt-${letra}`} />
                </div>
              );
            })}
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-600 mb-1 block">Orden (0 = al final)</label>
            <input type="number" min="0" value={form.orden}
              onChange={e => setForm({ ...form, orden: parseInt(e.target.value) || 0 })}
              className="w-20 px-2 py-1 border border-slate-200 rounded text-sm" />
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="text-xs px-3 py-1.5 border border-slate-300 rounded">Cancelar</button>
          <button type="submit" disabled={saving}
            className="text-xs font-semibold px-4 py-1.5 bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-60"
            data-testid="save-pregunta">
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}
